import { ReactElement, ReactNode, useEffect } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import produce from 'immer'
import cloneDeep from 'lodash-es/cloneDeep'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import usePlanTablesQueryKey from '@editor/hooks/usePlanTablesQueryKey'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getPlanTables } from '@queries'
import type { PlanTable, PlanTableInput } from 'src/domain/planTables/types'
import { usePersistentParams } from 'src/hooks'
import { useFilterViewStore } from '../filterViewStore'
import { completePlanTableInput, planTableDtoToPlanTable } from './utils'

interface PlanTableState {
  isVisible: boolean
  isActive: boolean
  tables: PlanTable[]
  activeFilterViewId: string | null
  pendingTemplateKey: string | null
}

const initialState: PlanTableState = {
  isVisible: false,
  isActive: false,
  tables: [],
  activeFilterViewId: null,
  pendingTemplateKey: null,
}

const usePlanTableStore = create(
  combine(cloneDeep(initialState), set => ({
    clear: () => set(cloneDeep(initialState)),

    setIsActive: (isActive: boolean) =>
      set(
        produce((state: PlanTableState) => {
          state.isActive = isActive
          state.tables = state.tables.map(table => ({ ...table, isActive }))
        }),
      ),

    setIsVisible: (isVisible: boolean) =>
      set(
        produce((state: PlanTableState) => {
          state.isVisible = isVisible
          state.tables = state.tables.map(table => ({ ...table, isVisible }))
        }),
      ),

    setTables: (tables: PlanTable[]) =>
      set(
        produce((state: PlanTableState) => {
          state.tables = tables
          state.isVisible = tables.some(table => table.isVisible)
          state.isActive = tables.some(table => table.isActive) || state.isActive
        }),
      ),

    updateTableAttributes: (
      tableId: string,
      attributes: Partial<
        Pick<
          PlanTable,
          | 'name'
          | 'position'
          | 'table'
          | 'isVisible'
          | 'isActive'
          | 'planConfig'
          | 'templateOptions'
        >
      >,
    ) =>
      set(
        produce((state: PlanTableState) => {
          const index = state.tables.findIndex(table => table.id === tableId)
          if (index === -1) return
          state.tables[index] = { ...state.tables[index], ...attributes }
          state.isVisible = state.tables.some(table => table.isVisible)
          state.isActive = state.tables.some(table => table.isActive) || state.isActive
        }),
      ),

    addTable: (input: PlanTableInput) =>
      set(
        produce((state: PlanTableState) => {
          if (!state.activeFilterViewId) return
          state.tables.push(
            completePlanTableInput(
              {
                ...input,
                isActive: input.isActive ?? state.isActive,
                isVisible: input.isVisible ?? true,
              },
              state.activeFilterViewId,
            ),
          )
          state.isVisible = state.tables.some(table => table.isVisible)
          state.isActive = state.tables.some(table => table.isActive) || state.isActive
        }),
      ),

    removeTable: (tableId: string) =>
      set((state: PlanTableState) => ({
        ...state,
        tables: state.tables.filter(table => table.id !== tableId),
        isVisible: state.tables
          .filter(table => table.id !== tableId)
          .some(table => table.isVisible),
        isActive: state.tables.filter(table => table.id !== tableId).some(table => table.isActive),
      })),

    setActiveFilterViewId: (activeFilterViewId: string | null) => set({ activeFilterViewId }),

    setPendingTemplateKey: (pendingTemplateKey: string | null) => set({ pendingTemplateKey }),
  })),
)

interface PlanTableStoreProviderProps {
  children: ReactNode
}

const PlanTableStoreProvider = ({ children }: PlanTableStoreProviderProps): ReactElement => {
  const clear = usePlanTableStore(state => state.clear)
  const setTables = usePlanTableStore(state => state.setTables)
  const setActiveFilterViewId = usePlanTableStore(state => state.setActiveFilterViewId)
  const activeFilterViewId = useFilterViewStore(state => state.activeFilterViewId)
  const { projectId }: { projectId?: string } = usePersistentParams()
  const { enqueueSnackbar } = useSnackbar()
  const planTablesQueryKey = usePlanTablesQueryKey(projectId)

  useEffect(() => {
    setActiveFilterViewId(activeFilterViewId)
  }, [activeFilterViewId, setActiveFilterViewId])

  useQuery({
    queryKey: planTablesQueryKey,
    queryFn: () => getPlanTables.request(projectId),
    enabled: !!projectId,
    onSuccess: data => {
      setTables(data.map(planTableDtoToPlanTable))
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Plan-Tabellen'), {
        variant: 'error',
      })
      setTables([])
    },
  })

  useEffect(() => {
    return () => {
      clear()
    }
  }, [clear])

  return <>{children}</>
}

export { PlanTableStoreProvider, usePlanTableStore }
